import BackgroundImage from "@components/BackgroundImageCst";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import AssociationTableCst from "@components/AssociationTableCst";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { ServicesURL, getQueryString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { usePostDetail } from "@hooks/useFetchDetail";
import { useNavigate } from "react-router-dom";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";

const DashboardEnablement: React.FC = () => {
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    key?: string | number;
  }>();
  const { t } = useTranslation(["dashboard-enablement-page", "common"]);
  const [dashboarAssociation, setDashboardAssociation] = useState<any>([]);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trigger } = usePostDetail(ServicesURL.enableDisableDashboard);
  const { mutate } = useSWRConfig();
  const isPO = user?.roles.includes(RolesDigitedEnum.PRODUCT_OWNER);

  const { data: questionsData } = useSWR(
    [ServicesURL.getMetabaseQuestions, { pageIndex: 0, pageSize: 1000 }],
    ([url, filters]: any) =>
      fetcher(getQueryString(url, filters), MethodType.GET),
    { revalidateOnFocus: false },
  );

  const { dataTable } = useFetchTable(
    `${ServicesURL.getDashboard}?getUsersAndRoles=true&getCorporates=true${isPO ? "" : `&userId=${user?.id_user}`}`,
    PagesID["dashboard.corporate-association"],
  );

  const onDashboardAssociation = async (selected: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToDashboardAssociation: any = Object.keys(selected).map(
      (key: string) => {
        if (key.includes("||")) {
          return {
            id: key.split("||")[0],
            label: key.split("||")[1],
          };
        }
      },
    );

    const initialSelectionFormatted = objToDashboardAssociation.map(
      (item: any) => {
        return item;
      },
    );

    const listToSend = objToDashboardAssociation.filter(
      (obj1: any) =>
        !dataTable.output.some(
          (obj2: any) => String(obj1.id) === String(obj2.metabaseQuestionId),
        ),
    );

    const modifiedList = listToSend.map((item: any) => {
      return item;
    });

    const idsExist = dataTable.output
      .filter(
        (item: any) =>
          item.metabaseQuestionId !== null &&
          item.metabaseQuestionId !== undefined,
      )
      .map((item: any) => {
        return {
          id: String(item.metabaseQuestionId),
          label: item.originalName,
        };
      });

    const idsToAdd = modifiedList.filter(
      (item: any) => !idsExist.includes(Number(item)),
    );

    const idsToDelete = idsExist.filter(
      (itemToSend: any) =>
        !initialSelectionFormatted.some(
          (detail: any) => detail.id === itemToSend.id,
        ),
    );

    const toSend: any = [...idsToAdd, ...idsToDelete];

    const arrayFinal = toSend.map((item: any) => {
      const question = Array.isArray(questionsData?.output)
        ? questionsData.output.find(
            (q: any) => String(q.dashboardId) === String(item.id),
          )
        : null;
      const filters = question?.parameters
        ? Object.values(question.parameters).map((param: any) => ({
            metabaseParamKey: param.slug,
            olUserField: param.slug,
            operator: param.operator || "EQUALS",
            valueType: param.valueType || "STRING",
            required: param.required ?? true,
            defaultValue: null,
          }))
        : [];
      return {
        id: item.id,
        associatedCorporates: null,
        associatedRoles: null,
        associatedUsersNumber: null,
        openLearningPlatformName: null,
        originalName: item.label,
        quicksightUrl: null,
        metabaseQuestionId: item.id,
        filters,
      };
    });

    try {
      await trigger(arrayFinal);
      setIsReady(false);
      mutate(
        (key: any) => {
          const k = Array.isArray(key) ? key[0] : key;
          return typeof k === "string" && k.includes(ServicesURL.getDashboard);
        },
        undefined,
        { revalidate: true },
      );
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      console.error(error);
      setOpenToast(undefined);
    }
  };

  useEffect(() => {
    if (!dataTable || !questionsData) return;
    const association = dataTable?.output
      .filter(
        (item: any) =>
          item.metabaseQuestionId !== null &&
          item.metabaseQuestionId !== undefined,
      )
      .map((item: any) => {
        const question = Array.isArray(questionsData?.output)
          ? questionsData.output.find(
              (q: any) =>
                String(q.dashboardId) === String(item.metabaseQuestionId),
            )
          : null;
        if (!question) return null;
        const isDisabled =
          !!item.associatedCorporates?.length ||
          !!item.associatedRoles?.length ||
          item.associatedUsersNumber > 0;
        return {
          id: question.dashboardId,
          label: question.name,
          disabled: isDisabled,
        };
      })
      .filter(Boolean);
    setDashboardAssociation(association);
    setIsReady(true);
  }, [dataTable, questionsData]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-dashboard.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        {!isReady && <FormLoadingStateCst />}
        {!!isReady && (
          <AssociationTableCst
            association={dashboarAssociation}
            setAssociation={null}
            callBackAssociation={onDashboardAssociation}
            setCloseAssociation={null}
            service={`${ServicesURL.getMetabaseQuestions}`}
            pageID={PagesID["dashboard.enablement"]}
            title={""}
            enableMultiRowSelection={true}
            defaultFilters={{ pageIndex: 0, pageSize: 1000 }}
            customBackAction={() => navigate(-1)}
            hiddenFilters={true}
          />
        )}

        <ModalConfirmsCst
          open={!!openToast}
          title={t("hero-title-add")}
          description={""}
          onCallBackClosed={() => {
            setOpenToast(undefined);
          }}
          status={openToast}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default DashboardEnablement;
